export function previewUrlTokenizer(obj) {
  const { appKey } = obj
  const key = obj.key ? `?key=${obj.key}` : ''
  const width = obj.width ? `&width=${obj.width}` : ''
  const height = obj.height ? `&height=${obj.height}` : ''
  const globalTheme = obj.globalTheme === 'dark' ? '&dark_mode=true' : ''
  const hideUi = obj.hideUi ? '&hide_ui=true' : ''
  const URI = obj.isPage ? `${obj.key}?` : `/component${key}`
  return `https://${appKey}${URI}${width}${height}${hideUi}${globalTheme}`
}

export function screenshotTokenizer(obj, opts = {}) {
  // FA204 / frank trap — moved from module scope into this function (sole
  // consumer, pure literal — see longPress.js for the identical shape).
  const DEF_OPTIONS = {
    selector: true,
    lazy_load: true,
    omit_background: true,
    output: true,
    retina: true,
    filetype: 'webp'
  }

  const { appKey } = obj
  const key = obj.key ? `?key=${obj.key}` : ''
  const width = obj.width && !opts.pdf ? `&width=${obj.width}` : ''
  const height = obj.height && !opts.pdf ? `&height=${obj.height}` : ''
  const hideUi = obj.hideUi ? `&hide_ui=${true}` : ''
  const URI = obj.isPage ? `${obj.key}?` : `/component${key}`
  const screenshot = opts.pdf ? '' : '&screenshot=true'
  const componentUrl = encodeURIComponent(
    `https://${appKey}${URI}${width}${height}${hideUi}${screenshot}`
  )
  const userAgent =
    'user_agent=Mozilla%2F5.0%20(Macintosh%3B%20Intel%20Mac%20OS%20X%2010_15_7)%20AppleWebKit%2F537.36%20(KHTML%2C%20like%20Gecko)%20Chrome%2F128.0.0.0%20Safari%2F537.36'
  const params = [
    'token=SGG5Q3D-WZN437D-NCC3ZZ2-Q15ZMZG',
    `url=${componentUrl}`,
    userAgent,
    !opts.pdf && width,
    !opts.pdf && height,
    `dark_mode=${obj.globalTheme === 'dark'}`,
    'fresh=true',
    (opts || DEF_OPTIONS).output && 'output=image',
    opts.pdf
      ? 'file_type=pdf&pdf_options%5Bformat%5D)=A4&pdf_options%5Bmedia%5D)=print&pdf_options%5Blandscape%5D)=false&pdf_options%5Bprint_background%5D)=true&pdf_options%5Bpage_range%5D)=1-50&full_page=true&page_range=1-50&delay=350'
      : `file_type=${opts?.filetype || DEF_OPTIONS.filetype || 'webp'}`,
    'wait_for_event=domcontentloaded',
    (opts || DEF_OPTIONS).retina && 'retina=true',
    (opts || DEF_OPTIONS).selector && 'selector=%5Bkey%3D%22Component%22%5D',
    (opts || DEF_OPTIONS).lazy_load && 'lazy_load=true',
    (opts || DEF_OPTIONS).omit_background && 'omit_background=true'
  ]
    .filter((v) => v)
    .join('&')
  return `https://shot.screenshotapi.net/screenshot?&${params}`
}

export function imgToBase64Url(img) {
  // Encode the SVG string to Base64
  const base64 = window.btoa(unescape(encodeURIComponent(img)))

  // Create a data URL with the Base64-encoded SVG
  const base64Url = `data:image/svg+xml;base64,${base64}`

  return base64Url
}

// export function svgToBase64Url (svg) {
//   // Encode the SVG string to Base64
//   const base64 = window.btoa(unescape(encodeURIComponent(svg)))

//   // Create a data URL with the Base64-encoded SVG
//   const base64Url = `data:image/svg+xml;base64,${base64}`

//   return base64Url
// }

export async function createRemoteScreenshot(opts = {}) {
  const data = this.getCanvasScope()
  const { selected } = data
  if (!selected?.node) {
    return
  }

  const { appKey, sceneTheme } = this.getRootState()
  const { key } = this.state
  const width = selected.node.offsetWidth
  const height = selected.node.offsetHeight
  const isPage = key.startsWith('/')
  const factoryKey = isPage ? 'pages' : 'components'

  const API_URL = this.call(
    'screenshotTokenizer',
    {
      appKey,
      isPage,
      key,
      width,
      height,
      retina: false,
      hideUi: true,
      globalTheme: sceneTheme
    },
    {
      pdf: opts.pdf,
      output: false
    }
  )

  const response = await window
    .fetch(API_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then((response2) => response2.json())

  response.smblsUrl = response.screenshot?.replace(
    'https://storage.screenshotapi.net/',
    'https://screenshots.api.symbols.app/'
  )

  // el.log(API_URL)
  // el.log(response)

  const screenshot = opts.pdf ? { pdf: response } : { thumbnail: response }

  this.getRootState().quietUpdate(
    {
      schema: {
        [factoryKey]: { [key]: screenshot }
      }
    },
    {
      message: `Updated screenshot on "${key}"`
    }
  )

  this.state.quietUpdate(screenshot)
  this.lookup('ComponentEditor').update()
  this.lookup('EditorPanels').state.quietReplace(screenshot)
}

export async function createMainPageScreenshot(changes) {
  const pages = changes?.schema?.pages
  const projectsLength = Object.keys(pages || {}).length
  const MainPage = pages?.['/']
  const onlyMainPage = MainPage && projectsLength === 1 && !changes?.schema?.components
  if (!onlyMainPage) {
    return
  }

  const { appKey, sceneTheme } = this.getRootState()
  const API_URL = this.call('screenshotTokenizer', {
    appKey,
    isPage: true,
    key: '/',
    width: 460 * 2,
    height: 280 * 2,
    retina: false,
    hideUi: true,
    globalTheme: sceneTheme
  })

  const response = await window
    .fetch(API_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then((response2) => response2.json())

  // el.log(API_URL)
  // el.log(response)
  const thumbnail = response.screenshot?.replace(
    'https://storage.screenshotapi.net/',
    'https://screenshots.api.symbols.app/'
  )

  this.getRootState().quietUpdate(
    { thumbnail },
    {
      preventUpdate: true,
      saveOnly: true,
      preventShModalUpdate: true,
      preventContentUpdate: true,
      message: 'Updated project thumbnail'
    }
  )

  const activeProj = this.getRootState('projects').filter((v) => v.key === appKey)[0]
  activeProj.thumbnail = thumbnail
}
